import type { ExpandCastOptions } from './expand';
import type { SerializeOptions } from './serialize/serializeOptions';

/**
 * One place for every Cast "context" option, so each surface's configuration is
 * visible together. Two distinct kinds, kept as separate groups:
 *
 *  - SERIALIZE (emit) options — *what to render*. A small set of shared policies.
 *    The format prompt (`createCastContextPrompt`) must describe exactly the set
 *    used, and a client-serialized selection must match server-serialized tool
 *    output within one prompt, so these are deliberately few and shared.
 *
 *  - EXPAND options — *how much to retrieve* around RAG hits. One preset per
 *    surface. Presets carry only the STATIC knobs (`expandNeighborCount`,
 *    `maxTokenBudget`, `tableTokenLimit`); `estimateTokens` (model-dependent) and
 *    any dynamic budget are supplied at the call site by spreading the preset.
 */

// ─────────────────────────────────────────────────────────────────────────────
// SERIALIZE (emit) options
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The single, canonical serialize-options set for ALL document context fed to
 * chat agents — selection mentions (serialized client-side), document-tool
 * output, and RAG context (serialized server-side).
 *
 * Why one set rather than per-agent: a selection mention is serialized on the
 * client *before the handling agent is known*, so if a read-only agent dropped
 * (say) `text` from its tool/RAG output, the selection block in that same
 * prompt would still carry `text` — the two surfaces would disagree. A single
 * set keeps every agent's view internally consistent, which is the whole point
 * of flipping the chat surfaces to HTML together.
 *
 * Always pair this with `createCastContextPrompt(DOCUMENT_CONTEXT_SERIALIZE_OPTIONS)`
 * so the format documentation describes exactly what is emitted. Per-agent /
 * model-driven options are a deliberate future step.
 *
 * Spelled out explicitly (rather than spread from `DEFAULT_SERIALIZE_OPTIONS`)
 * so it reads as a deliberate policy snapshot, decoupled from the library
 * default.
 */
export const DOCUMENT_CONTEXT_SERIALIZE_OPTIONS: SerializeOptions = {
  emitFontFamily: false,
  emitFontSize: false,
  emitInlineStyles: true,
  emitLinks: true,
  emitParagraphText: true,
  emitRedlines: true,
  emitSkipped: true,
};

/**
 * The lean serialize-options set for read-only, non-chat extraction jobs
 * (relationship extraction, question extraction, type/parties, playbooks) — the
 * Cast successor to the old pipe `serializeExtractedParagraphs(..., { plain: true })`
 * ("a single ORIGINAL line": text only, no styling, no edits).
 *
 * Why a separate, leaner set than `DOCUMENT_CONTEXT_SERIALIZE_OPTIONS`: those
 * jobs read the document to extract facts and never co-occur with a client-side
 * selection mention in one prompt, so they carry none of the chat constraint
 * that forces one heavy shared set. Dropping inline styles, the `final`
 * attribute (they don't anchor redlines), redlines, and fonts keeps the token
 * footprint close to the old `plain` output. Structure the model still needs —
 * `<p id>`, lists, `<table>/<tr>/<td>` — is unaffected by these flags. Links
 * (`emitLinks`) stay on: a link's destination is extractable content (parties,
 * references, citations may live in the href), not appearance to be stripped.
 * Pair with `createCastContextPrompt(EXTRACTION_CONTEXT_SERIALIZE_OPTIONS)` so
 * the format documentation matches what is emitted.
 *
 * Edge case: with `emitRedlines: false`, a paragraph carrying Aline tracked
 * changes renders its deleted and inserted run text concatenated (only the
 * `<ins>`/`<del>` tags are dropped), which is neither the old ORIGINAL nor the
 * accepted state. This does not arise for the extraction corpus (freshly
 * uploaded contracts have no tracked changes), so every run is plain and the
 * output is exactly the original text.
 */
export const EXTRACTION_CONTEXT_SERIALIZE_OPTIONS: SerializeOptions = {
  emitFontFamily: false,
  emitFontSize: false,
  emitInlineStyles: false,
  emitLinks: true,
  emitParagraphText: false,
  emitRedlines: false,
  emitSkipped: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPAND (retrieval) options
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The static knobs of an expand preset. `estimateTokens` is omitted because it
 * is model-dependent and supplied at the call site:
 *   `expandAndSerializeCast(cast, ids, { ...PRESET, estimateTokens }, serialize)`.
 * A dynamic `maxTokenBudget` (e.g. a per-candidate budget) is likewise spread
 * over the preset at the call site.
 */
type ExpandPreset = Omit<ExpandCastOptions, 'estimateTokens'>;

/**
 * RAG query within a single document — the chat query tool and report
 * question-extraction. A tight neighbourhood around each hit.
 */
export const DOCUMENT_QUERY_EXPAND_OPTIONS = {
  expandNeighborCount: 1,
  maxTokenBudget: 10_000,
  perTableTokenLimit: 500,
} satisfies ExpandPreset;

/**
 * Relationship extraction + matching RAG (properties extraction, resolve
 * relationships). Wider neighbourhood and a generous budget to capture recitals
 * and cross-references; resolve-relationships overrides `maxTokenBudget` with a
 * per-candidate budget.
 */
export const RELATIONSHIP_EXPAND_OPTIONS = {
  expandNeighborCount: 3,
  maxTokenBudget: 100_000,
  perTableTokenLimit: 1_000,
} satisfies ExpandPreset;

/**
 * Client chat selection mention — a couple of neighbour paragraphs around the
 * selected span. Its only consumer, `serializeSelectionFromRange`, runs
 * client-side with no tokenizer and no known target model, so it injects
 * `estimateTokens: text => text.length`. Every budget below is therefore a
 * **character** count, not tokens — `perTableTokenLimit` caps characters per
 * table, and `maxTokenBudget` would cap total characters.
 *
 * `maxTokenBudget` stays `Infinity` on purpose: a finite cap would truncate the
 * user's selected text itself (selected paragraphs are budgeted together with
 * their expansion), while the expansion is structurally small — about
 * `expandNeighborCount` paragraphs on each side plus a few headings/parents.
 * `perTableTokenLimit` still guards the one part that can grow without bound: a
 * large table.
 */
export const SELECTION_EXPAND_OPTIONS = {
  expandNeighborCount: 2,
  maxTokenBudget: Infinity,
  perTableTokenLimit: 10_000, // characters per table, not tokens (see above)
} satisfies ExpandPreset;

/**
 * Serialize only the matched paragraphs, no neighbour padding or budget cap —
 * for surfaces that pre-filter to the exact relevant set before serializing
 * (type/parties extraction).
 */
export const MATCHED_ONLY_EXPAND_OPTIONS = {
  expandNeighborCount: 0,
  maxTokenBudget: Infinity,
  perTableTokenLimit: Infinity,
} satisfies ExpandPreset;
